import { STAP_VALIDEREN, STAP_VASTLEGGEN } from './kerncijfers';
import type { StapKeuze } from './stapFilter';
import type { HandlingTimeCategorieBlok, HandlingTimeCategorieRegel } from './handlingTime.api';

/**
 * Story 34.1 — het rekenwerk onder het blok "waar gaat de tijd heen per soort product".
 *
 * Hier staat uitsluitend rekenwerk en geen opmaak, om dezelfde reden als bij `kerncijfers.ts`,
 * `tabel.ts`, `stapFilter.ts` en `aandachtslijst.ts`: de scherpste regels van deze story zijn
 * rekenregels, en die horen navraagbaar op één plek te staan.
 *
 * > [!note] Drie besluiten van 31 augustus 2026 sturen dit bestand
 * > **Ondergrens tien stuks** · **de tien in vaste volgorde plus één hoop** · **het etiket ligt
 * > hier vast en komt niet uit de gegevens.**
 */

/**
 * De tien categorieën, in de volgorde waarin ze op het scherm staan, met hun code uit
 * `gpcProductCategoryMapping`.
 *
 * HET ETIKET STAAT HIER EN NIET IN DE GEGEVENS (AC-2). De vertaaltabel draagt op 31 augustus 2026
 * twintig namen op zeventien codes, met spellingsvarianten voor precies hetzelfde: `Zelfzorg
 * geneesmiddelen` naast `zelfzorggeneesmiddelen`, `Bijzondere voedingsmiddelen` naast `speciale
 * voeding`, `Overig` naast `non-food`. Zou het scherm de naam uit de gegevens halen, dan hing de
 * spelling af van welk product er toevallig eerst langskwam.
 *
 * De bron groepeert al op de CODE en levert de elf regels in deze volgorde; deze lijst is er om de
 * naam bij de code te zetten, en om te bewaken dat de volgorde niet stil verandert.
 */
export const PRODUCTCATEGORIEEN = [
  { code: 'DRYGR', naam: 'Levensmiddelen' },
  { code: 'DRUGS', naam: 'Zelfzorggeneesmiddelen' },
  { code: 'MEDDEV', naam: 'Medische hulpmiddelen' },
  { code: 'SUPPL', naam: 'Voedingssupplementen' },
  { code: 'SPECFOOD', naam: 'Bijzondere voedingsmiddelen' },
  { code: 'COSMETICS', naam: 'Cosmetica, persoonlijke verzorging, gezondheidsproducten' },
  { code: 'BIOCIDES', naam: 'Biociden' },
  { code: 'DETER', naam: 'Detergenten' },
  { code: 'PETFOOD', naam: 'Diervoeding' },
  { code: 'BATTERY', naam: 'Batterijen' },
] as const;

/** De naam bij een code, of `null` voor de elfde regel; die naam staat in de vertalingen. */
export function naamVoorCategorie(code: string | null): string | null {
  if (code === null) return null;
  return PRODUCTCATEGORIEEN.find((categorie) => categorie.code === code)?.naam ?? null;
}

/**
 * Onder hoeveel stuks een regel géén gemiddelde toont maar een streepje.
 *
 * TIEN, en dat is een besluit en geen berekening (AC-9): onder tien stuks is "minuten per stuk"
 * geen kengetal maar een toevalstreffer. Het aantal en de totale tijd staan er wél — alleen de
 * deling ontbreekt.
 *
 * Een stuk is hier hetzelfde als overal op dit scherm: een ONDERSCHEIDEN PRODUCT binnen de gekozen
 * stap (AC-0), precies waar de aandachtslijst erboven door deelt.
 *
 * > [!warning] Dit wijkt bewust af van de aandachtslijst erboven
 * > Die kent geen enkele ondergrens en rangschikt vandaag op een gemiddelde uit één behandeling.
 * > Dat is geen inconsistentie maar een verschil in wat de twee doen: de aandachtslijst wijst een
 * > PERSOON aan en draagt daarom de eerlijkheidsnoot, dit blok wijst een CATEGORIE aan en heeft die
 * > noot niet. Of de aandachtslijst dezelfde ondergrens hoort te krijgen is een eigen vraag en
 * > staat open.
 */
export const ONDERGRENS_GEMIDDELDE = 10;

/** Eén regel van het blok, zoals het scherm hem toont. */
export interface CategorieRegel {
  /** `null` is *overige categorieën*: de elfde regel, altijd als laatste. */
  code: string | null;
  /** Onderscheiden producten in deze categorie, binnen deze stap. Nul is een geldige waarde (AC-8). */
  stuks: number;
  /** De totale gemeten tijd van deze categorie, in minuten. */
  minuten: number;
  /**
   * Minuten gedeeld door stuks, of `null` onder de ondergrens en bij nul stuks (AC-9).
   *
   * ONAFGEROND, precies zoals de aandachtslijst het doet: afronden gebeurt pas bij het tonen.
   */
  gemiddeldePerStuk: number | null;
}

/** Het blok van één stap. */
export interface CategorieBlok {
  stap: StapKeuze;
  regels: CategorieRegel[];
  /** De som van de elf regels: hetzelfde getal als de gemeten tijd van díe stap (AC-10). */
  totaleMinuten: number;
  /** De som van de elf regels aan stuks: het aantal onderscheiden producten van díe stap. */
  totaleStuks: number;
}

/** Van de woorden van de bron naar de woorden op het scherm. */
const STAP_VOOR_BRONNAAM: Record<string, StapKeuze> = {
  [STAP_VASTLEGGEN]: 'vastleggen',
  [STAP_VALIDEREN]: 'valideren',
};

/**
 * De verwachte volgorde van de elf regels, zoals de bron ze hoort te leveren.
 *
 * Het scherm HERSCHIKT niet, en dat is met opzet: zou het dat wel doen, dan zou een bron die de
 * volgorde stil verandert hier ongemerkt goedgepraat worden. In plaats daarvan wordt de volgorde
 * hier afgedwongen door de regels op te zoeken — een categorie die de bron niet meestuurt komt als
 * nul in beeld, en alles wat de bron stuurt en hier niet in staat valt in *overige*.
 */
const VERWACHTE_CODES: readonly (string | null)[] = [
  ...PRODUCTCATEGORIEEN.map((categorie) => categorie.code),
  null,
];

/**
 * Het gemiddelde per stuk, of `null` zodra er niet genoeg stuks zijn om er een te noemen.
 *
 * Nul stuks levert `null` en nooit een deling door nul. Sinds AC-0 op onderscheiden producten telt
 * kán een regel met minuten geen nul stuks meer hebben — een gemeten minuut hoort bij een product —
 * maar de grens van tien vangt dat geval sowieso af, en een deling die er niet is kan niet ontsporen.
 */
function gemiddeldeVoor(minuten: number, stuks: number): number | null {
  if (stuks < ONDERGRENS_GEMIDDELDE) return null;
  return minuten / stuks;
}

/**
 * De regels van één blok, in de vaste volgorde van deze story.
 *
 * WAT DE BRON NIET STUURT KOMT ALS NUL IN BEELD (AC-8), en wat zij stuurt maar hier niet bekend is
 * telt op bij *overige categorieën* — nooit stil weg (AC-7). Die tweede regel is de reden dat deze
 * functie de aangeleverde regels optelt in plaats van ze op te zoeken en de rest te negeren: een
 * elfde categorie die de bron ooit toevoegt zou anders zijn minuten meenemen, en dan telt het blok
 * niet meer op tot de gemeten tijd.
 */
function bouwRegels(aangeleverd: readonly HandlingTimeCategorieRegel[]): CategorieRegel[] {
  const perCode = new Map<string | null, { stuks: number; minuten: number }>();
  for (const code of VERWACHTE_CODES) perCode.set(code, { stuks: 0, minuten: 0 });

  for (const regel of aangeleverd) {
    const code = VERWACHTE_CODES.includes(regel.categoryCode) ? regel.categoryCode : null;
    const bak = perCode.get(code)!;
    // OPTELLEN MAG HIER, en dat is geen slordigheid met een ontdubbeling: de bron levert al
    // ontdubbelde producten per categorie, en één product hoort bij precies één categorie. Twee
    // regels die hier samenkomen zijn dus altijd disjuncte verzamelingen producten.
    bak.stuks += regel.tradeItems;
    bak.minuten += regel.minutes;
  }

  return VERWACHTE_CODES.map((code) => {
    const bak = perCode.get(code)!;
    return {
      code,
      stuks: bak.stuks,
      minuten: bak.minuten,
      gemiddeldePerStuk: gemiddeldeVoor(bak.minuten, bak.stuks),
    };
  });
}

/**
 * De blokken van het scherm, beperkt tot de gekozen stap.
 *
 * Op `beide` levert dit TWEE blokken onder elkaar op, elk met hun eigen elf regels, en nooit één
 * blok waarin de twee stappen zijn opgeteld (AC-11) — om dezelfde reden waarom de aandachtslijst er
 * twee lijsten van maakt: het zijn ongelijksoortige handelingen.
 *
 * Een stap die de bron niet meestuurt krijgt GEEN blok. Dat betekent dat er in die stap geen minuut
 * gemeten is, en een blok van elf nullen zou daar een preciezer verhaal van maken dan het is.
 */
export function bouwCategorieBlokken(
  blokken: readonly HandlingTimeCategorieBlok[] | undefined,
  stappen: readonly StapKeuze[],
): CategorieBlok[] {
  if (blokken === undefined) return [];

  const resultaat: CategorieBlok[] = [];
  for (const stap of stappen) {
    const bron = blokken.find((blok) => STAP_VOOR_BRONNAAM[blok.handling] === stap);
    if (bron === undefined) continue;

    const regels = bouwRegels(bron.lines);
    resultaat.push({
      stap,
      regels,
      totaleMinuten: regels.reduce((totaal, regel) => totaal + regel.minuten, 0),
      totaleStuks: regels.reduce((totaal, regel) => totaal + regel.stuks, 0),
    });
  }
  return resultaat;
}
